// ============================================================
// Blog post registry
// ============================================================
// Single source of truth for the blog. The index page (Blog.js) reads the
// metadata; the post page (BlogPost.js) renders the body via bodyHtml
// (dangerouslySetInnerHTML). The prerender script injects bodyHtml into
// the static <noscript> so Googlebot sees the full article on first-pass crawl.
//
// To add a post:
//   1. Create src/blog/bodies/<slug>.js exporting the article as an HTML string
//   2. Import it below and add an entry to `posts` with all metadata + bodyHtml
//
// `date` is ISO (YYYY-MM-DD) for structured data; `dateDisplay` is the
// human-readable form. `description` is the meta/OG description and the dek
// shown on the index card, kept under ~160 chars for SEO.

import agentBossHtml from './bodies/agent-boss';
import againstTheGiantHtml from './bodies/against-the-giant';
import engineModelBakeoffHtml from './bodies/engine-model-bakeoff';
import governanceTheaterHtml from './bodies/governance-theater';
import rightModelRightTaskHtml from './bodies/right-model-right-task';
import silentToolCallsHtml from './bodies/silent-tool-calls';

export const posts = [
    {
        slug: 'agent-boss',
        title: 'Everyone\'s an Agent Boss Now. Did Anyone Ask the Workers?',
        subtitle:
            'The AI industry promoted us all to manager of agents. The research says supervising AI is mostly specification and inspection, and the skilled operators we overlook may be best at it.',
        description:
            'AI vendors say every employee becomes an agent boss. Management talent is rare, and the evidence says supervising AI rewards craft expertise and inspection over leadership.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-10-07',
        dateDisplay: 'October 7, 2026',
        readingTime: '7 min read',
        heroImage: '/blog/agent-boss.jpg',
        heroAlt:
            'Illustration of a craftsperson at a wooden workbench inspecting a circuit board through a loupe while a long row of idle robot workers waits behind him under faint org-chart lines.',
        bodyHtml: agentBossHtml,
    },
    {
        slug: 'governance-theater',
        title: 'Why Your Company\'s AI Strategy Is Security Theater',
        subtitle:
            'Nobody chooses to build governance theater. The incentives make it the rational move. What that costs, and the one question that separates real governance from the imitation.',
        description:
            'AI Ethics Committees exist. Permission to say no does not. Why 95% of enterprise AI pilots fail, the recognition-vs-mitigation gap, and the diagnostic that separates real governance from theater.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-08-17',
        dateDisplay: 'August 17, 2026',
        readingTime: '9 min read',
        heroImage: '/blog/governance-theater.jpg',
        heroAlt:
            'Overhead flat-lay of a printed slide deck fanned across a dark walnut boardroom table. Each slide has elegant gold-embossed titles (Vision, Pillars, Principles, Roadmap) but the body areas are blank.',
        bodyHtml: governanceTheaterHtml,
    },
    {
        slug: 'right-model-right-task',
        title: 'The Right Model for the Right Task (And Why "Pick a Good Model" Is the Wrong Advice)',
        subtitle:
            'At simple complexity, two models were identical. At moderate complexity, one held 10/10 while the other frayed to 7/10. Neither is "better." They fail at different points on the curve.',
        description:
            '"Pick a good model" is incomplete advice. Models fail at different complexity thresholds in non-obvious ways. Why the first inference broker had four design errors, and why per-execution attribution is the foundation — not a feature.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-07-10',
        dateDisplay: 'July 10, 2026',
        readingTime: '8 min read',
        heroImage: '/blog/right-model-right-task.jpg',
        heroAlt:
            'Overhead flat-lay of five distinct tools arranged in a row on a dark steel workbench — power drill, handsaw, utility knife, screwdrivers, and wrench — each purpose-built for a specific job.',
        bodyHtml: rightModelRightTaskHtml,
    },
    {
        slug: 'engine-model-bakeoff',
        title: 'We Tested Every Agentic Coding Engine Against Every Local Model. The Results Were Weird.',
        subtitle:
            'A 6B flash model beat a 32B coder model. aider:whole went 0/10 on a model that aider:diff cleared 10/10. The variable wasn\'t model quality.',
        description:
            'A structured bake-off of 5 coding agents × 7 local models reveals that edit-format discipline and protocol compliance matter more than parameter count.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-06-29',
        dateDisplay: 'June 29, 2026',
        readingTime: '8 min read',
        heroImage: '/blog/engine-model-bakeoff.jpg',
        heroAlt:
            'A large matrix grid of glowing tiles — rows labeled with model names, columns with engine names — showing a non-uniform pattern of green, amber, and red pass-rate results.',
        bodyHtml: engineModelBakeoffHtml,
    },
    {
        slug: 'silent-tool-calls',
        title: 'Your Coding Agent Is Silently Doing Nothing',
        subtitle:
            'No error. No timeout. Just nothing. How a broken serving layer masquerades as a bad model — and why the fix belongs one layer down.',
        description:
            'Most qwen-family models return tool calls as plain text on raw Ollama endpoints. Agentic tools discard it silently. Here\'s the data, the failure modes, and the fix.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-06-19',
        dateDisplay: 'June 19, 2026',
        readingTime: '7 min read',
        heroImage: '/blog/silent-tool-calls.jpg',
        heroAlt:
            'A glowing data pipeline diagram showing broken tool-call connections between an AI coding agent and a raw Ollama endpoint, with dropped JSON fragments in the negative space.',
        bodyHtml: silentToolCallsHtml,
    },
    {
        slug: 'against-the-giant',
        title: 'Against the Giant',
        subtitle:
            'There is honor in a desperate war. A crusade to win your AI back from giants worth more than nations — on your own ground, by your own rules.',
        description:
            'Frontier-lab AI charges you three coins — gold, secrets, and your own audit trail. Here is the third road: run open models on ground you own, governed and measured.',
        author: 'Andrew Schwabe',
        authorTitle: null,
        date: '2026-06-09',
        dateDisplay: 'June 9, 2026',
        readingTime: '9 min read',
        heroImage: '/blog/against-the-giant.jpg',
        heroAlt:
            'A lone knight in worn but dignified armor stands calmly in the middle of a narrow road, facing a towering giant at dawn.',
        bodyHtml: againstTheGiantHtml,
    },
];

export function getPost(slug) {
    return posts.find((p) => p.slug === slug) || null;
}

// Most-recent-first ordering for the index page.
export const postsByDate = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));

// External essays published elsewhere. Listed on the blog index as outbound
// cards; they are NOT routed internally (no /blog/:slug page) and carry no
// internal canonical, so they don't compete with the hosted posts for indexing.
export const externalPosts = [
    {
        title: 'Alignment-Driven Development',
        subtitle: 'The orchestration framework AI-paired coding forgot to build.',
        href: 'https://hackernoon.com/alignment-driven-development-the-orchestration-framework-ai-paired-coding-forgot-to-build',
        source: 'HackerNoon',
        image: '/blog/alignment-driven-development.jpg',
        imageAlt: 'A conductor leading an orchestra of musicians with glowing lightbulbs for heads.',
    },
    {
        title: 'Why Small Models Matter in a Network of Experts Era',
        subtitle: 'Distributed Network of Experts — API-linked large and small AI models to drive innovation and accuracy.',
        href: 'https://hackernoon.com/why-small-models-matter-in-a-network-of-experts-era',
        source: 'HackerNoon',
        image: '/blog/why-small-models-matter.jpg',
        imageAlt: 'Abstract network diagram showing large and small AI model nodes linked across a distributed inference graph.',
    },
    {
        title: 'We Built a Local Model Arena in 30 Minutes — Infrastructure Mattered More Than the App',
        subtitle: 'A hands-on tutorial building a Model Arena app that pits local LLMs against each other through LLMesh.',
        href: 'https://hackernoon.com/we-built-a-local-model-arena-in-30-minutes-infrastructure-mattered-more-than-the-app',
        source: 'HackerNoon',
        image: '/blog/local-model-arena.jpg',
        imageAlt: 'Two AI model robots facing off in an arena, surrounded by infrastructure and server racks.',
    },
    {
        title: 'Streaming Faster Made Our LLM Hub Slower',
        subtitle: "One POST per LLM token kills multi-user throughput. Here's the 258-line adaptive batcher that fixed it.",
        href: 'https://hackernoon.com/streaming-faster-made-our-llm-hub-slower',
        source: 'HackerNoon',
        image: '/blog/streaming-faster-llm-hub.jpg',
        imageAlt: 'A data stream visualization showing token throughput with a bottleneck resolved by an adaptive batcher.',
    },
];
